import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useLocaleStore } from '@/stores/localeStore';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? 'https://terminal.ailab.am/api',
  headers: {
    Accept: 'application/json',
  },
});

api.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = useLocaleStore.getState().locale;
  return config;
});

// X-Company-Id must match the logged-in user's own company — the backend
// 403s on a mismatch. Platform routes are cross-tenant and stay unscoped;
// when the user isn't loaded yet the backend resolves the tenant from the
// user row, so omitting the header is always safe.
api.interceptors.request.use((config) => {
  const url = config.url ?? '';
  // An explicitly provided header wins: the platform console scopes
  // per-request into a chosen tenant (e.g. employee photos of any company).
  if (config.headers['X-Company-Id'] != null) return config;
  const companyId = useAuthStore.getState().user?.company?.id;
  const isPlatform = url.startsWith('/platform/') || url.startsWith('/api/platform/');
  if (!isPlatform && companyId != null) {
    config.headers['X-Company-Id'] = String(companyId);
  } else {
    delete config.headers['X-Company-Id'];
  }
  return config;
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 interceptor — clear auth state and redirect to /login when a protected
// request comes back unauthorised. Skip auth endpoints to avoid redirect loops.
api.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const url = error.config?.url ?? '';
      const isAuthUrl =
        url.startsWith('/auth/') ||
        url.startsWith('/sanctum/') ||
        url.includes('/auth/login') ||
        url.includes('/auth/me');

      if (!isAuthUrl) {
        useAuthStore.getState().clear();

        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  },
);
